/**
 * Telegram launch helpers. Reads the WebApp global injected by telegram-web-app.js
 * (loaded in index.html). Phase 2 may migrate to @telegram-apps/sdk-react.
 */

export interface TelegramUserInfo {
  id?: number;
  firstName: string;
  lastName?: string;
  username?: string;
  languageCode?: string;
  photoUrl?: string;
}

export function getWebApp(): TelegramWebApp | undefined {
  return window.Telegram?.WebApp;
}

/** True when actually running inside Telegram (initData present). */
export function isInTelegram(): boolean {
  return Boolean(getWebApp()?.initData);
}

export function getRawInitData(): string | undefined {
  const data = getWebApp()?.initData;
  return data && data.length > 0 ? data : undefined;
}

/** Launch deep-link param (Mini App `?startapp=…` / bot `?start=…`) — carries the
 *  referral token (e.g. "ref_<userId>") on a first launch. */
export function getStartParam(): string | undefined {
  const p = (getWebApp()?.initDataUnsafe as { start_param?: string } | undefined)?.start_param;
  return typeof p === 'string' && p.length > 0 ? p : undefined;
}

/** Open a t.me link via Telegram (so the share sheet opens in-app), falling back
 *  to a normal new tab outside Telegram. */
export function openTelegramLink(url: string): void {
  try {
    const wa = getWebApp() as unknown as { openTelegramLink?: (u: string) => void } | undefined;
    if (wa?.openTelegramLink) {
      wa.openTelegramLink(url);
      return;
    }
  } catch {
    /* fall through to window.open */
  }
  try {
    window.open(url, '_blank');
  } catch {
    /* no-op */
  }
}

/**
 * Telegram identity for UI prefill (name, language, photo).
 * DISPLAY ONLY — never trust client-side initDataUnsafe for auth decisions;
 * the server re-validates the signed initData (docs/15-auth-and-sessions.md).
 */
export function getTelegramUser(): TelegramUserInfo | undefined {
  const u = getWebApp()?.initDataUnsafe?.user;
  if (!u) return undefined;
  return {
    id: u.id,
    firstName: u.first_name ?? '',
    lastName: u.last_name,
    username: u.username,
    languageCode: u.language_code,
    photoUrl: u.photo_url,
  };
}

export function readyAndExpand(): void {
  const wa = getWebApp();
  wa?.ready();
  wa?.expand();
  applyTelegramInsets();
  // Re-apply when the device/Telegram safe areas change or fullscreen toggles
  // (Bot API 8.0+). Guarded — older clients simply never fire these.
  const ev = wa as unknown as { onEvent?: (e: string, cb: () => void) => void } | undefined;
  ev?.onEvent?.('safeAreaChanged', applyTelegramInsets);
  ev?.onEvent?.('contentSafeAreaChanged', applyTelegramInsets);
  ev?.onEvent?.('fullscreenChanged', applyTelegramInsets);
  ev?.onEvent?.('viewportChanged', applyTelegramInsets);
}

/**
 * Push Telegram's safe-area insets into CSS vars so the UI clears the status bar
 * AND Telegram's own controls — critical in FULLSCREEN mode, where the WebView's
 * env(safe-area-inset-*) read 0 but Telegram draws its header/close button over
 * the app. Total inset = device safe area (safeAreaInset) + the area Telegram's
 * UI occupies inside it (contentSafeAreaInset). When neither is available (old
 * client / browser) we leave the vars alone so the CSS env() defaults apply.
 */
export function applyTelegramInsets(): void {
  try {
    const wa = getWebApp() as unknown as {
      safeAreaInset?: { top?: number; bottom?: number; left?: number; right?: number };
      contentSafeAreaInset?: { top?: number; bottom?: number; left?: number; right?: number };
    } | undefined;
    if (!wa) return;
    const sa = wa.safeAreaInset;
    const ca = wa.contentSafeAreaInset;
    if (!sa && !ca) return; // old client → keep the env() fallbacks
    const root = document.documentElement.style;
    const set = (name: string, a?: number, b?: number) =>
      root.setProperty(name, `${Math.max(0, (a ?? 0) + (b ?? 0))}px`);
    set('--safe-top', sa?.top, ca?.top);
    set('--safe-bottom', sa?.bottom, ca?.bottom);
    set('--safe-left', sa?.left, ca?.left);
    set('--safe-right', sa?.right, ca?.right);
  } catch {
    /* never let inset wiring break boot */
  }
}

/**
 * Fire Telegram haptic feedback, mapping a small set of intent kinds onto the
 * WebApp HapticFeedback API (Bot API 6.1+). Fully guarded: when there's no
 * Telegram WebApp (browser/demo) or the device doesn't support haptics, this is
 * a silent no-op and never throws.
 *
 *  - 'spark'   → a strong, celebratory buzz (success notification) — the super-like
 *  - 'success' → success notification
 *  - 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' → an impact of that strength
 *  - 'warning' | 'error' → the matching notification feedback
 *  - 'select'  → the lightweight selection-changed tick
 */
export type HapticKind =
  | 'spark'
  | 'success'
  | 'warning'
  | 'error'
  | 'select'
  | 'light'
  | 'medium'
  | 'heavy'
  | 'rigid'
  | 'soft';

export function haptic(kind: HapticKind = 'light'): void {
  try {
    // HapticFeedback isn't in our minimal TelegramWebApp typing — read it off the
    // live global through a loose cast so this stays self-contained (no shared
    // .d.ts edit) and tolerant of older Telegram clients that lack the API.
    const hf = (getWebApp() as unknown as {
      HapticFeedback?: {
        impactOccurred?: (style: string) => void;
        notificationOccurred?: (type: string) => void;
        selectionChanged?: () => void;
      };
    } | undefined)?.HapticFeedback;
    if (!hf) return;
    switch (kind) {
      case 'spark':
      case 'success':
        hf.notificationOccurred?.('success');
        break;
      case 'warning':
      case 'error':
        hf.notificationOccurred?.(kind);
        break;
      case 'select':
        hf.selectionChanged?.();
        break;
      default:
        // 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' impacts
        hf.impactOccurred?.(kind);
        break;
    }
  } catch {
    /* never let haptics break a tap — unsupported device / older client */
  }
}

/**
 * Get a coarse device location. Telegram webviews often block
 * navigator.geolocation, so prefer Telegram's LocationManager (Bot API 8.0+),
 * then fall back to the browser API. Resolves null when unavailable/denied.
 */
export function requestLocation(): Promise<{ lat: number; lng: number; accuracy?: number } | null> {
  return new Promise((resolve) => {
    const done = (v: { lat: number; lng: number; accuracy?: number } | null) => resolve(v);
    const lm = getWebApp()?.LocationManager;
    if (lm) {
      try {
        lm.init(() => {
          if (!lm.isLocationAvailable) return fallback();
          lm.getLocation((loc) => {
            if (loc) done({ lat: loc.latitude, lng: loc.longitude, accuracy: loc.horizontal_accuracy ?? undefined });
            else fallback();
          });
        });
        return;
      } catch {
        /* fall through */
      }
    }
    fallback();

    function fallback() {
      try {
        navigator.geolocation?.getCurrentPosition(
          (pos) => done({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
          () => done(null),
          { timeout: 7000, maximumAge: 600000 },
        );
        if (!navigator.geolocation) done(null);
      } catch {
        done(null);
      }
    }
  });
}
